import { useCallback, useEffect, useMemo, useRef } from "react";
import { Navigate, NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import FunctionalButton from "components/layout/FunctionalButton";
import AboutDevicePanel from "components/control/settings/AboutDevicePanel";
import DeveloperSettingsPanel from "components/control/settings/DeveloperSettingsPanel";
import ContactUsPanel from "components/control/settings/ContactUsPanel";
import TelegramNotificationSettingsPanel from "components/control/settings/TelegramNotificationSettingsPanel";
import RobotSettingsPanel from "components/control/settings/RobotSettingsPanel";
import NavigationBehaviourSettingsPanel from "components/control/settings/NavigationBehaviourSettingsPanel";
import NavigationSettingsPanel from "components/control/settings/NavigationSettingsPanel";
import ObjectRecognitionSettingsPanel from "components/control/settings/ObjectRecognitionSettingsPanel";
import AccessibilitySettingsPanel from "components/control/settings/AccessibilitySettingsPanel";
import LanguageSettingsPanel from "components/control/settings/LanguageSettingsPanel";
import WifiSettingsPanel from "components/control/settings/WifiSettingsPanel";
import EthernetSettingsPanel from "components/control/settings/EthernetSettingsPanel";
import MobileNetworkSettingsPanel from "components/control/settings/MobileNetworkSettingsPanel";
import PayloadSettingsPanel from "components/control/settings/PayloadSettingsPanel";
import OpenSpaceSettingsPanel from "components/control/settings/OpenSpaceSettingsPanel";
import EmailNotificationsSettingsPanel from "components/control/settings/EmailNotificationsSettingsPanel";
import UserManagementPanel from "components/control/settings/UserManagementPanel";
import { ReactComponent as ArrowLeftIcon } from "assets/svgs/ArrowLeft.svg";
import {
    useDeveloperMode,
} from "providers/DeveloperModeProvider";
import { useToast } from "providers/ToastProvider";
import { useDeviceFeatureFlags } from "providers/DeviceFeatureFlagsProvider";

const getTabConfig = ({
    COMPLETE_ROBOT_PROFILE,
    HAS_ETHERNET,
    HAS_MISSIONS,
    HAS_NAV,
    HAS_OBJECT_RECOGNITION,
    HAS_TELEGRAM_NOTIFICATION,
}) => [
    { labelKey: "tabs.wifi", path: "wifi", Panel: WifiSettingsPanel },
    ...(HAS_ETHERNET
        ? [{ labelKey: "tabs.ethernet", path: "ethernet", Panel: EthernetSettingsPanel }]
        : []),
    ...(COMPLETE_ROBOT_PROFILE
        ? [{ labelKey: "tabs.robot", path: "robot", Panel: RobotSettingsPanel }]
        : []),
    ...(HAS_NAV
        ? [
              { labelKey: "tabs.navigation", path: "navigation", Panel: NavigationSettingsPanel },
              {
                  labelKey: "tabs.navigationBehaviour",
                  path: "navigation-behaviour",
                  Panel: NavigationBehaviourSettingsPanel,
              },
          ]
        : []),
    { labelKey: "tabs.payload", path: "payload", Panel: PayloadSettingsPanel },
    { labelKey: "tabs.openspace", path: "openspace", Panel: OpenSpaceSettingsPanel },
    { labelKey: "tabs.mobileNetwork", path: "mobile-network", Panel: MobileNetworkSettingsPanel },
    ...(HAS_TELEGRAM_NOTIFICATION
        ? [{ labelKey: "tabs.telegramNotification", path: "telegram-notification", Panel: TelegramNotificationSettingsPanel }]
        : []),
    ...(HAS_MISSIONS
        ? [{ labelKey: "tabs.emailNotifications", path: "email-notifications", Panel: EmailNotificationsSettingsPanel }]
        : []),
    { labelKey: "tabs.userManagement", path: "user-management", Panel: UserManagementPanel },
    ...(HAS_OBJECT_RECOGNITION
        ? [{ labelKey: "tabs.objectRecognition", path: "object-recognition", Panel: ObjectRecognitionSettingsPanel }]
        : []),
    ...(HAS_NAV
        ? [{ labelKey: "tabs.accessibility", path: "accessibility", Panel: AccessibilitySettingsPanel }]
        : []),
    { labelKey: "tabs.language", path: "language", Panel: LanguageSettingsPanel },
    { labelKey: "tabs.aboutDevice", path: "about-device", Panel: AboutDevicePanel },
    { labelKey: "tabs.contactUs", path: "contact-us", Panel: ContactUsPanel },
];

const DEVELOPER_TAB = {
    labelKey: "tabs.developer",
    path: "developer",
    Panel: DeveloperSettingsPanel,
} as const;

const ABOUT_DEVICE_PATH = "about-device";
const DEVELOPER_PATH = DEVELOPER_TAB.path;

const DeviceSettingsPage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("settings");
    const { addToast } = useToast();
    const { developerModeEnabled, registerAboutDeviceTap } = useDeveloperMode();
    const { flags } = useDeviceFeatureFlags();
    const {
        COMPLETE_ROBOT_PROFILE,
        HAS_ETHERNET,
        HAS_MISSIONS,
        HAS_NAV,
        HAS_OBJECT_RECOGNITION,
        HAS_TELEGRAM_NOTIFICATION,
    } = flags;
    const tabConfig = useMemo(() => getTabConfig(flags), [flags]);
    const hasAnnouncedDeveloperMode = useRef(false);

    useEffect(() => {
        if (!developerModeEnabled || hasAnnouncedDeveloperMode.current) return;
        hasAnnouncedDeveloperMode.current = true;
        addToast({ type: "success", message: t("developerModeEnabled") });
    }, [addToast, developerModeEnabled, t]);

    const handleAboutDeviceNavClick = useCallback(() => {
        if (!HAS_NAV || developerModeEnabled) return;
        registerAboutDeviceTap();
    }, [developerModeEnabled, HAS_NAV, registerAboutDeviceTap]);

    const visibleTabs = useMemo(() => {
        if (!HAS_NAV || !developerModeEnabled) return [...tabConfig];
        const aboutIndex = tabConfig.findIndex((tab) => tab.path === ABOUT_DEVICE_PATH);
        return [
            ...tabConfig.slice(0, aboutIndex + 1),
            DEVELOPER_TAB,
            ...tabConfig.slice(aboutIndex + 1),
        ];
    }, [developerModeEnabled, HAS_NAV, tabConfig]);

    const navLinks = useMemo(
        () =>
            visibleTabs.map(({ labelKey, path }) => (
                <NavLink
                    key={path}
                    to={path}
                    replace
                    onClick={path === ABOUT_DEVICE_PATH ? handleAboutDeviceNavClick : undefined}
                    className={({ isActive }) =>
                        `w-full rounded-r5 px-3 py-3 text-left text-h5 ${
                            isActive
                                ? "bg-black80 text-white"
                                : "bg-transparent text-black40 hover:bg-black90 hover:text-white"
                        } ${path === DEVELOPER_PATH ? "text-primary2" : ""}`
                    }
                >
                    {t(labelKey)}
                </NavLink>
            )),
        [handleAboutDeviceNavClick, t, visibleTabs]
    );

    const routes = useMemo(
        () =>
            visibleTabs.map(({ path, Panel }) => (
                <Route key={path} path={path} element={<Panel />} />
            )),
        [visibleTabs]
    );

    return (
        <div className="flex h-full min-h-0 w-full flex-col">
            <div className="flex shrink-0 items-center justify-between border-b border-black80 px-4 py-3">
                <h2 className="text-h3 font-semibold text-white">{t("title")}</h2>
                <FunctionalButton
                    icon={<ArrowLeftIcon className="h-4 w-4 fill-white" />}
                    className="h-9 w-9 shrink-0 rounded-r5 border border-primary2 bg-primary2 hover:bg-primary1"
                    onClick={() => navigate("..")}
                />
            </div>
            <div className="flex min-h-0 min-w-0 flex-1 flex-row gap-0 overflow-hidden">
                <nav
                    aria-label={t("navAriaLabel")}
                    className="flex min-h-0 w-[13.5rem] shrink-0 flex-col gap-1 overflow-y-auto border-r border-black80 py-3 pl-4 pr-3"
                >
                    {navLinks}
                </nav>
                <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-y-contain bg-black90 p-4">
                    <Routes>
                        {routes}
                        <Route index element={<Navigate to={visibleTabs[0].path} replace />} />
                        {!HAS_NAV ? (
                            <Route path="navigation" element={<Navigate to={visibleTabs[0].path} replace />} />
                        ) : null}
                        {!HAS_NAV ? (
                            <Route
                                path="navigation-behaviour"
                                element={<Navigate to={visibleTabs[0].path} replace />}
                            />
                        ) : null}
                        {!HAS_NAV ? (
                            <Route
                                path="accessibility"
                                element={<Navigate to={visibleTabs[0].path} replace />}
                            />
                        ) : null}
                        {!HAS_TELEGRAM_NOTIFICATION ? (
                            <Route
                                path="telegram-notification"
                                element={<Navigate to={visibleTabs[0].path} replace />}
                            />
                        ) : null}
                        {!HAS_ETHERNET ? (
                            <Route path="ethernet" element={<Navigate to={visibleTabs[0].path} replace />} />
                        ) : null}
                        {!HAS_MISSIONS ? (
                            <Route
                                path="email-notifications"
                                element={<Navigate to={visibleTabs[0].path} replace />}
                            />
                        ) : null}
                        {!HAS_OBJECT_RECOGNITION ? (
                            <Route
                                path="object-recognition"
                                element={<Navigate to={visibleTabs[0].path} replace />}
                            />
                        ) : null}
                        {!COMPLETE_ROBOT_PROFILE ? (
                            <Route path="robot" element={<Navigate to={visibleTabs[0].path} replace />} />
                        ) : null}
                        {!HAS_NAV || !developerModeEnabled ? (
                            <Route path={DEVELOPER_PATH} element={<Navigate to={ABOUT_DEVICE_PATH} replace />} />
                        ) : null}
                        <Route path="insta360" element={<Navigate to="payload" replace />} />
                        <Route path="update" element={<Navigate to={visibleTabs[0].path} replace />} />
                    </Routes>
                </div>
            </div>
        </div>
    );
};

export default DeviceSettingsPage;
